import { LockClosedIcon } from "@heroicons/react/24/solid";
import { getConsumptionColour, getConsumptionTextColour } from "../home/Home";

/**
 * The "Read" / "Watch" / "Listen" pill in a content card's footer.
 *
 * This is the only control that opens the content: the card body around it is
 * inert (`openOnCardClick={false}`), so the pill has to look and behave like a
 * button rather than the badge it used to be.
 */
export default function ConsumptionButton({
  consumption,
  title,
  onClick,
  opensInNewTab = true,
  locked = false,
}: {
  consumption: string;
  title: string;
  onClick: (e?: React.MouseEvent) => void;
  /**
   * Whether the click leaves the page. Videos now play in a modal, and announcing
   * a new tab that never opens is worse than announcing nothing.
   */
  opensInNewTab?: boolean;
  /** Members-only content: the pill asks for a sign-in instead of opening. */
  locked?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={locked ? `Members only: ${title}` : `${consumption}: ${title}`}
      aria-label={
        locked
          ? `Sign in to ${consumption.toLowerCase()} "${title}"`
          : `${consumption} "${title}" ${
              opensInNewTab ? "(opens in a new tab)" : "(plays on this page)"
            }`
      }
      className={`
        flex items-center shrink-0
        text-${getConsumptionTextColour(consumption)} text-sm font-bold
        px-4 py-1 bg-${getConsumptionColour(consumption)}
        rounded-full shadow-sm
        transition-all duration-200 ease-out
        hover:scale-105 hover:-translate-y-0.5 hover:shadow-md hover:brightness-95
        active:scale-95 active:translate-y-0 active:shadow-sm active:brightness-90
        focus:outline-none focus-visible:ring-2 focus-visible:ring-dark-blue focus-visible:ring-offset-1
        motion-reduce:transform-none motion-reduce:transition-none
      `}
    >
      {locked ? (
        <>
          <LockClosedIcon className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          Sign in to {consumption.toLowerCase()}
        </>
      ) : (
        consumption
      )}
    </button>
  );
}
